import { AuthenticationService } from '@openeducation/hr-rn-cognito-services';
import { useEffect, useState } from 'react';

export type SpotfireSessionState =
  | 'idle'
  | 'loading'
  | 'ready'
  | 'error'
  | 'expired';

// Fallback when the handshake response carries no expiresInSeconds.
const DEFAULT_TTL_SECONDS = 3600;
// Retry cadence after a failed renewal (the current cookie stays valid until its TTL).
const RENEWAL_RETRY_DELAY_MS = 60_000;

/**
 * Owns the Spotfire relay session for the lifetime of the embed screen. POSTs
 * {relay-origin}/spotfire-session with the Cognito access token before the embed mounts — the
 * response sets the SpotfireSession cookie (Path=/spotfire) that authorizes every proxied
 * /spotfire/** request at the edge, and reports its TTL. The handshake is then re-POSTed at
 * half-TTL (sliding renewal; each renewal re-checks JWT validity and entitlement), plus
 * immediately when a background tab whose timers were throttled becomes visible past its
 * renewal time. On unmount the cookie is revoked (best-effort DELETE) — the HttpOnly cookie
 * would otherwise outlive logout, idle logout, and purchaser switches.
 *
 * On initial failure the embed is still attempted (direct, un-gated Spotfire servers don't
 * serve the handshake endpoint) and no renewals are scheduled. Failed renewals are retried
 * while the current cookie is still valid; once its TTL lapses without a successful renewal
 * the state becomes 'expired' (terminal) so the caller can surface an error instead of a
 * dead embed.
 */
export const useSpotfireSession = (
  serverUrl: string | undefined | null,
  purchaserId: string | undefined | null
): SpotfireSessionState => {
  const [state, setState] = useState<SpotfireSessionState>('idle');

  useEffect(() => {
    if (!serverUrl || !purchaserId) {
      setState('idle');
      return;
    }

    let cancelled = false;
    let established = false;
    let renewing = false;
    let nextRenewalAt = Infinity;
    let cookieExpiresAt = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const handshake = async (): Promise<number> => {
      const tokens = await AuthenticationService.getTokens();
      if (!tokens) {
        throw new Error('no authenticated session');
      }
      const origin = new URL(serverUrl).origin;
      const params = new URLSearchParams({
        salesforcePurchaserId: purchaserId,
      });
      const response = await fetch(
        `${origin}/spotfire-session?${params.toString()}`,
        {
          method: 'POST',
          headers: { Authorization: tokens.accessToken },
          credentials: 'include',
        }
      );
      if (!response.ok) {
        throw new Error(`spotfire-session responded ${response.status}`);
      }
      const body = await response.json().catch(() => null);
      const expiresInSeconds = body?.expiresInSeconds;
      const ttlSeconds =
        typeof expiresInSeconds === 'number' && expiresInSeconds > 0
          ? expiresInSeconds
          : DEFAULT_TTL_SECONDS;
      cookieExpiresAt = Date.now() + ttlSeconds * 1000;
      return ttlSeconds;
    };

    const scheduleRenewal = (delayMs: number) => {
      nextRenewalAt = Date.now() + delayMs;
      timer = setTimeout(async () => {
        if (renewing) {
          return;
        }
        renewing = true;
        try {
          const expiresInSeconds = await handshake();
          if (!cancelled) {
            scheduleRenewal((expiresInSeconds / 2) * 1000);
          }
        } catch (e) {
          console.warn('Spotfire session renewal failed', e);
          if (!cancelled) {
            if (Date.now() >= cookieExpiresAt) {
              // The cookie lapsed with no successful renewal — further retries can't revive it.
              nextRenewalAt = Infinity;
              setState('expired');
            } else {
              scheduleRenewal(RENEWAL_RETRY_DELAY_MS);
            }
          }
        } finally {
          renewing = false;
        }
      }, delayMs);
    };

    // Background tabs throttle timers, so a renewal can be long overdue when the tab becomes
    // visible again — fire it immediately.
    const onVisibilityChange = () => {
      if (
        document.visibilityState === 'visible' &&
        established &&
        Date.now() >= nextRenewalAt
      ) {
        clearTimeout(timer);
        scheduleRenewal(0);
      }
    };

    setState('loading');

    (async () => {
      try {
        const expiresInSeconds = await handshake();
        if (cancelled) {
          return;
        }
        established = true;
        setState('ready');
        scheduleRenewal((expiresInSeconds / 2) * 1000);
      } catch (e) {
        console.warn('Spotfire session handshake failed', e);
        if (!cancelled) {
          setState('error');
        }
      }
    })();

    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      if (established) {
        const origin = new URL(serverUrl).origin;
        fetch(`${origin}/spotfire-session`, {
          method: 'DELETE',
          credentials: 'include',
        }).catch(() => undefined);
      }
    };
  }, [serverUrl, purchaserId]);

  return state;
};
